<template>
  <Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
    <DialogContent class="max-w-7xl max-h-[95vh] h-full bg-white/80 p-1 flex flex-col">
      <!-- Div for transparent border -->
      <div class="bg-white rounded-md h-full flex flex-col">
        <DialogHeader class="mb-4 border-b border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between p-4">
            <div>
              <DialogTitle>Application Details</DialogTitle>
              <DialogDescription> View and manage application details </DialogDescription>
            </div>

            <!-- Tab Buttons -->
            <JobApplicationTabNavigation
              v-model:active-tab="activeTab"
              :referral-eligible="applicantProfileStore.applicantProfile?.referral_package?.referral_eligible"
            />
          </div>
        </DialogHeader>

        <!-- Application Content -->
        <div class="w-full flex-1 overflow-hidden">
          <!-- Loading State -->
          <div
            v-if="applicationsStore.isLoadingSelected || isInitialLoading"
            class="flex items-center justify-center h-full min-h-72"
          >
            <LoaderCircle class="animate-spin mr-2" />Loading...
          </div>

          <!-- No Application Found State -->
          <div
            v-else-if="
              !selectedApplication && !applicationsStore.isLoadingSelected && initialLoadAttempted
            "
            class="flex items-center justify-center h-full min-h-72"
          >
            <HeartCrack class="h-5 w-5 mr-2" />
            No application found
          </div>

          <!-- Application Content -->
          <div v-else-if="selectedApplication" class="h-full">
            <!-- Job Application Tab Content -->
            <JobApplicationTabContent
              v-if="activeTab === 'application'"
              :selected-application="selectedApplication"
              :applicant-profile="applicantProfileStore.applicantProfile"
              :application-id="props.applicationId"
              @preview-email="activeTab = 'email_preview'"
              @send-email="handleSendEmail"
              @application-updated="handleApplicationUpdated"
            />

            <!-- Referral Package Tab Content -->
            <section v-else-if="activeTab === 'referral'" class="h-full overflow-y-auto">
              <ReferralPackageContent v-if="applicantProfileStore.applicantProfile" />
              <div v-else class="p-4 text-center text-gray-500">No referral package available</div>
            </section>

            <!-- Email Preview Tab Content -->
            <EmailPreviewTabContent
              v-if="activeTab === 'email_preview' && props.applicationId"
              :application-id="props.applicationId"
              :is-modal="true"
              :on-success="() => { console.log('JobApplicationModal onSuccess called'); $emit('update:isOpen', false) }"
              @email-sent="() => { console.log('JobApplicationModal received email-sent'); $emit('update:isOpen', false) }"
            />
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useApplicationsStore } from '@/stores/applications'
import { useApplicantProfileStore } from '@/stores/applicantProfile'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

import JobApplicationTabNavigation from '@/components/common/jobApplication/JobApplicationTabNavigation.vue'
import JobApplicationTabContent from '@/components/common/jobApplication/JobApplicationTabContent.vue'
import EmailPreviewTabContent from '@/components/common/jobApplication/EmailPreviewTabContent.vue'
import ReferralPackageContent from '@/components/referrals/ReferralPackageContent.vue'

import { HeartCrack, LoaderCircle } from 'lucide-vue-next'

const props = defineProps<{
  isOpen: boolean
  applicationId: string | null
  initialTab?: string
}>()

defineEmits<{
  'update:isOpen': [value: boolean]
}>()
const userStore = useUserStore()
const applicationsStore = useApplicationsStore()
const applicantProfileStore = useApplicantProfileStore()

const selectedApplication = ref<any>(null)
const isInitialLoading = ref(true)
const initialLoadAttempted = ref(false)
const activeTab = ref(props.initialTab || 'application')

// Watch for application ID changes and fetch applicant profile
watch(
  () => props.applicationId,
  async (newId) => {
    if (newId) {
      isInitialLoading.value = true
      initialLoadAttempted.value = false
      // Reset to initial tab when opening with new application
      activeTab.value = props.initialTab || 'application'
      try {
        await applicationsStore.fetchApplicationById(newId)
        selectedApplication.value = applicationsStore.selectedApplication

        // Fetch applicant profile if we have user details
        if (selectedApplication.value?.user_details?.id) {
          await applicantProfileStore.fetchApplicantProfileById(
            selectedApplication.value.user_details.id
          )
        }

        isInitialLoading.value = false
        initialLoadAttempted.value = true
      } catch (error) {
        console.error('Failed to fetch application:', error)
        selectedApplication.value = null
        isInitialLoading.value = false
        initialLoadAttempted.value = true
      }
    } else {
      selectedApplication.value = null
      isInitialLoading.value = false
      initialLoadAttempted.value = true
    }
  },
  { immediate: true }
)

const handleSendEmail = async () => {
  console.log('sent email!')
}

const handleApplicationUpdated = (updatedApplication: any) => {
  selectedApplication.value = updatedApplication
  applicationsStore.selectedApplication = updatedApplication
}

onMounted(async () => {
  try {
    await userStore.fetchCurrentUser()
  } catch (error) {
    console.error('Failed to fetch user data:', error)
  }
})
</script>
